<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="d-flex align-center">
        <SitesSearchPopover
          :filters="{ canUseAIFeatures: false }"
          :multiple="false"
          :selectedSiteIds="assignedSites"
          :maxSelectableSites="1"
          :showAdvancedOption="false"
          @change="addSites"
        >
          <Button
            plain
            icon="mdi-plus"
            :text="$t('multiple_selector.add_site')"
          />
        </SitesSearchPopover>
      </div>

      <SearchInput
        :value="searchTerms"
        @change="searchTerms = $event"
        style="width: 250px;"
      />
    </div>

    <v-card outlined>
      <v-data-table
        calculate-widths
        data-private
        :footer-props="footerOptions"
        :headers="headers"
        :items="sites"
        :loading="isLoadingSites"
        :options.sync="options"
        :server-items-length="totalRecordsInData"
      >
        <template v-slot:[`item.name`]="{ item }">
          <span>{{ item?.name }}</span>
        </template>

        <template v-slot:[`item.country`]="{ item }">
          <span>
            {{
              item?.countryCodes?.length
                ? $t(`countries.${item.countryCodes[0]}`)
                : ''
            }}
          </span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <Button
            color="blueGrey"
            icon="mdi-trash-can-outline"
            @click.stop="handleSiteDelete(item)"
          />
        </template>
      </v-data-table>

      <DeleteConfirmationModal
        :show="isSettingsDeleteConfirmationShown"
        :title="$t('common.attention')"
        :text="
          $t(
            'ai_features.user_access_control.site_setting_delete_confirmation',
            { entityName: siteToDelete?.name }
          )
        "
        @cancel="cancelSiteDelete"
        @delete="confirmSiteDelete"
      />
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import api from '@/api'

import SearchInput from '@/components/Core/SearchInput.vue'
import Button from '@/components/UI/Button.vue'
import SitesSearchPopover from '@/components/SitesSearchPopover/SitesSearchPopover.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

const SORT_BY_FIELD = 'updatedAt'
const DEFAULT_SIZE = 50
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'SiteAccessTable',
  mixins: [mixin],

  components: {
    Button,
    DeleteConfirmationModal,
    SearchInput,
    SitesSearchPopover,
  },

  data() {
    return {
      isLoadingSites: false,
      totalRecordsInData: 0,
      searchTerms: '',
      sites: [],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'name',
          show: true,
        },
        {
          text: this.$t('common.country_one'),
          align: 'start',
          sortable: false,
          value: 'country',
          show: true,
        },
        {
          value: 'actions',
          text: this.$t('common.action_other'),
          sortable: false,
          show: true,
          align: 'start',
          width: 100,
        },
      ],
      siteToDelete: null,
      isSettingsDeleteConfirmationShown: false,
      assignedSites: [],
    }
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: 1,
        }
      },
      set(event) {
        this.fetchSitesWithAccessToAIFeatures({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  mounted() {
    this.fetchSitesWithAccessToAIFeatures({ page: 1 })
  },

  watch: {
    searchTerms() {
      this.fetchSitesWithAccessToAIFeatures({ page: 1 })
    },
  },

  methods: {
    async fetchSitesWithAccessToAIFeatures(options) {
      this.isLoadingSites = true

      const itemsPerPage = options.itemsPerPage || DEFAULT_SIZE
      const terms = this.searchTerms?.toLowerCase() ?? ''
      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : SORT_BY_FIELD, order: 'desc' }

      const filters = {
        canUseAIFeatures: true,
      }

      if (this.assignedSites?.length) {
        filters.sites = this.assignedSites
      }

      try {
        const response = await api.site.getOrganizationSites(
          this.organizationId,
          {
            filters,
            from: (options.page - 1) * itemsPerPage,
            limit: itemsPerPage,
            sort,
            terms: terms,
          }
        )

        this.sites = response?.data?.results ?? []
        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoadingSites = false
      }
    },

    async addSites({ ids }) {
      try {
        await api.ai.grantSitesAccessToAIFeatures(this.organizationId, ids)

        this.showSuccess(
          this.$t('ai_features.user_access_control.site_access_granted_success')
        )

        setTimeout(() => {
          this.fetchSitesWithAccessToAIFeatures({ page: 1 })
        }, 2000)
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.site_access_grant_error')
        )
      }
    },

    handleSiteDelete(site) {
      this.siteToDelete = site
      this.isSettingsDeleteConfirmationShown = true
    },

    confirmSiteDelete() {
      this.isSettingsDeleteConfirmationShown = false
      this.deleteSiteSettings(this.siteToDelete)
      this.siteToDelete = null
    },

    cancelSiteDelete() {
      this.isSettingsDeleteConfirmationShown = false
      this.siteToDelete = null
    },

    async deleteSiteSettings(site) {
      try {
        await api.ai.deleteSiteAISettings(this.organizationId, site._id)

        this.showSuccess(
          this.$t('ai_features.user_access_control.site_setting_delete_success')
        )

        setTimeout(() => {
          this.fetchSitesWithAccessToAIFeatures({ page: 1 })
        }, 2000)
      } catch (error) {
        this.showError(
          this.$t('ai_features.user_access_control.site_setting_delete_error')
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
